import { useState } from "react";
import { Form, Row, Col } from "react-bootstrap";
import GenericToggle from "@components/FormsV2/GenericToggle/GenericToggle";
import { PrivilegeSection } from "./PrivilegeSection";
import "./style.css";
import { PRIVILEGE_SCOPES, SCOPE_TITLES } from "../types";
import type { GroupedPrivileges } from "../api";
import type { RoleFormValues } from "../types";

export interface RoleFormProps {
  id: string;
  initialValues: RoleFormValues;
  definitions: GroupedPrivileges;
  isSubmitting: boolean;
  onSubmit: (values: RoleFormValues) => void;
}

export const RoleForm = ({
  id,
  initialValues,
  definitions,
  isSubmitting,
  onSubmit,
}: RoleFormProps): JSX.Element => {
  const [values, setValues] = useState<RoleFormValues>(initialValues);

  const setField = <K extends keyof RoleFormValues>(
    key: K,
    v: RoleFormValues[K]
  ) => setValues((prev) => ({ ...prev, [key]: v }));

  const setPrivilege = (privilegeId: string, level: string) =>
    setValues((prev) => ({
      ...prev,
      privileges: { ...prev.privileges, [privilegeId]: level },
    }));

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    onSubmit(values);
  };

  return (
    <Form id={id} data-testid={id} onSubmit={handleSubmit}>
      <Row>
        <Col sm={6}>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="role-name">Name</Form.Label>
            <Form.Control
              id="role-name"
              type="text"
              required
              value={values.name}
              onChange={(e) => setField("name", e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col sm={6}>
          <Form.Group className="mb-3">
            <Form.Label>Default Role</Form.Label>
            <div className="roleDefaultToggle">
              <GenericToggle
                key={`role-default-${initialValues.default}`}
                initialValueOfIsOn={values.default}
                onToggle={(v) => setField("default", v)}
                onText="Yes"
                offText="No"
                hasText
              />
            </div>
            <div className="small text-muted">
              When checked, this role is assigned to users auto-provisioned via
              SSO.
            </div>
          </Form.Group>
        </Col>
      </Row>

      {PRIVILEGE_SCOPES.map((scope) => (
        <PrivilegeSection
          key={scope}
          title={SCOPE_TITLES[scope]}
          definitions={definitions[scope] ?? []}
          values={values.privileges}
          onChange={setPrivilege}
        />
      ))}
    </Form>
  );
};

export default RoleForm;
